import React, { useEffect, useMemo, useState } from 'react';
import { useBasket } from '../../hooks';
import {
    StyledMiniBasketWrapper,
    StyledProgressBarWrapper,
    StyledMiniBasketContent,
    StyledMiniBaskedMainTitle,
    StyledMiniBasketImageWrap,
    StyledMiniBaskedProductInfo,
    StyledMiniBasketProductInfoDetails,
    StyledMiniBasketProductInfoTitle,
    StyledMiniBasketProductInfoBrand,
    StyledProductItem,
    StyledMiniBasketCloseIcon,
    StyledMiniBasketCloseWrapper,
    StyledMiniBaskedHeader,
    StyledMiniBasketCTAWrapper,
    StyledMiniBasketSimpleListWrapper,
    StyledProductItemWrapper,
} from './styled';
import { ProgressBar } from '~/shared/components/ProgressBar';
import { CTA } from '~/shared/components/ProductCard/components/CTA';
import Router from 'next/router';
import { useTranslation } from '~/shared/utils/translation';
import { getSummeryImage } from '~/utils/image-group-utils';
import { Image, Link } from '~/shared/components';
import { useFrame } from '~/shared/utils';
import useProducts from '~/features/commerce-api/hooks/useProducts';
import Checkmark from '~/icons/checkmark.svg';
import { Product } from '~/lib/data-contract/salesforce';
import { useRaptorProducts } from '~/services/raptor/useRaptorProducts';
import { Drawer } from '~/shared/components/Drawer';
import { SimpleProductList } from '~/shared/components/SimpleProductList';
import { useProductHandler } from '~/services/search/useProductHandler';
import { useScrollLock } from '~/shared/hooks';
import { ProductLoader } from './components/ProductLoader';
import ProductLoaderList from './components/ProductLoaderList';
import { GTM_LIST_NAME } from '~/shared/hooks/useEvents/useGTMEvents/models';

export const MiniBasket = () => {
    const {
        isMiniBasketOpen,
        toggleMiniBasket,
        productTotal,
        productItems = [],
        itemAccumulatedCount,
        getProductInBasket,
        addedProductId,
        setAddedProductId,
    } = useBasket();
    const {
        data: {
            settings,
            market: { minimumBasketTotalForFreeShipping },
        },
    } = useFrame();

    const { translate } = useTranslation();
    const getProductHandler = useProductHandler();
    const { handlers } = useProducts();
    const RAPTOR_GET_RELATED_ITEMS = 'GetRelatedItems';
    const {
        data: raptorProducts,
        isLoading,
        isError,
        refetch,
    } = useRaptorProducts({
        config: RAPTOR_GET_RELATED_ITEMS,
        preventAutoFetch: true,
        productId: addedProductId,
    });
    const count = itemAccumulatedCount();
    const [lastAddedProduct, setLastAddedProduct] = useState<Product[]>([]);

    const hasFreeShipping = handlers.getHasFreeShipping();
    const amountToFreeShipping = handlers.getAmountToFreeShipping();

    const totalForFreeShipping = Number(minimumBasketTotalForFreeShipping);

    // NOTE: Freight groups that are not eligible for free shipping (Not sure where to get these dynamically. But it's only used to show a different text IMC-145)
    const mandatoryFreightGroups = ['B', 'D', 'E', 'F', 'G'];

    const handleBasketClick = () => {
        Router.push(settings?.pageReferences?.basketPage?.url as string);

        toggleMiniBasket(false);
    };

    const isLastAddedProductHasMandatoryShipping = useMemo(
        () =>
            lastAddedProduct?.some((item) =>
                mandatoryFreightGroups.includes(item.c_ItemFreightGroup),
            ) || false,
        [lastAddedProduct, mandatoryFreightGroups],
    );

    const handleCloseClick = () => {
        toggleMiniBasket(false);
    };

    const { lock, unlock } = useScrollLock();
    const getLastAddedProduct = async () => {
        try {
            const params = {
                allImages: true,
                organizationId: '',
                siteId: '',
            };

            const productInBasket = await getProductInBasket(addedProductId, params);
            if (productInBasket && productInBasket?.length > 0) {
                setLastAddedProduct(productInBasket);
            }
        } catch (error) {
            console.warn('Something went wrong', error);
            toggleMiniBasket(false);
        }
    };

    useEffect(() => {
        if (isMiniBasketOpen) {
            lock();
        }
        return () => {
            unlock();
            setLastAddedProduct([]);
            setAddedProductId('');
        };
    }, [lock, unlock, isMiniBasketOpen]);

    useEffect(() => {
        if (addedProductId.length > 0) {
            // Handle both operations together to avoid rerender of item count
            const updateBasketData = async () => {
                refetch();
                await getLastAddedProduct();
            };
            updateBasketData();
        }
    }, [addedProductId]);

    useEffect(() => {
        // NOTE: If starting to navigate to a non product page, dismiss the minibasket (IMC-142)
        const handleRouteChangeStart = (url: string): void => {
            // NOTE: The general distinguishing factor that a page is a product is when it has an ID.
            const urlObj = new URL(url, window.location.origin);
            const id = urlObj.searchParams.get('id');
            if (!id) {
                toggleMiniBasket(false);
            }
        };
        Router.events.on('routeChangeStart', handleRouteChangeStart);
        return () => {
            Router.events.off('routeChangeStart', handleRouteChangeStart);
        };
    }, []);

    return (
        <Drawer open={isMiniBasketOpen} enableVerticalSlideMobile={true} onClose={handleCloseClick}>
            <StyledMiniBasketWrapper>
                <StyledMiniBasketContent>
                    <StyledMiniBaskedHeader>
                        <StyledMiniBaskedMainTitle>
                            <Checkmark />
                            {translate('basket.addToBasketSuccess')}
                        </StyledMiniBaskedMainTitle>
                        <StyledMiniBasketCloseWrapper>
                            <StyledMiniBasketCloseIcon
                                data-testid="close-minibasket-icon"
                                onClick={handleCloseClick}
                                tabIndex={0}
                                onKeyDown={(e: React.KeyboardEvent) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleCloseClick();
                                    }
                                }}
                            />
                        </StyledMiniBasketCloseWrapper>
                    </StyledMiniBaskedHeader>
                    <>
                        <StyledProductItemWrapper>
                            {lastAddedProduct?.length > 0 ? (
                                lastAddedProduct.map((lineItem: Product, index: number) => {
                                    const summeryImage = getSummeryImage(lineItem);

                                    return (
                                        <Link
                                            noHover
                                            href={lineItem.c_pageURL}
                                            onClick={handleCloseClick}
                                            key={lineItem?.itemId?.toString() ?? index}
                                        >
                                            <StyledProductItem>
                                                <StyledMiniBasketImageWrap>
                                                    {summeryImage && (
                                                        <Image
                                                            layout="fill"
                                                            sizes="300px"
                                                            src={summeryImage.link}
                                                            objectFit="contain"
                                                            alt={summeryImage.alt || lineItem.name}
                                                        />
                                                    )}
                                                </StyledMiniBasketImageWrap>

                                                <StyledMiniBaskedProductInfo>
                                                    <StyledMiniBasketProductInfoDetails>
                                                        <StyledMiniBasketProductInfoBrand>
                                                            {lineItem.brand}
                                                        </StyledMiniBasketProductInfoBrand>
                                                        <StyledMiniBasketProductInfoTitle>
                                                            {lineItem.name}
                                                        </StyledMiniBasketProductInfoTitle>
                                                    </StyledMiniBasketProductInfoDetails>
                                                </StyledMiniBaskedProductInfo>
                                            </StyledProductItem>
                                        </Link>
                                    );
                                })
                            ) : (
                                <StyledProductItem>
                                    <ProductLoader />
                                </StyledProductItem>
                            )}
                        </StyledProductItemWrapper>
                        <StyledMiniBasketCTAWrapper>
                            <CTA
                                disabled={false}
                                text={`${translate('basket.goToBasket')} (${count})`}
                                onClick={handleBasketClick}
                            />
                        </StyledMiniBasketCTAWrapper>
                        {productTotal ? (
                            <StyledProgressBarWrapper>
                                <h3>
                                    {hasFreeShipping
                                        ? translate('basket.basket.freeShipping.summary')
                                        : amountToFreeShipping &&
                                          translate('basket.basket.forFreeShipping', {
                                              amount: String(amountToFreeShipping),
                                          })}
                                </h3>
                                {hasFreeShipping || amountToFreeShipping ? (
                                    <ProgressBar
                                        progressValue={Math.min(productTotal, totalForFreeShipping)}
                                        max={totalForFreeShipping}
                                    />
                                ) : (
                                    <h3>
                                        {isLastAddedProductHasMandatoryShipping
                                            ? translate('basket.addedMandatoryShippingItem')
                                            : lastAddedProduct.length
                                              ? translate('basket.hasMandatoryShippingItem')
                                              : ''}
                                    </h3>
                                )}
                            </StyledProgressBarWrapper>
                        ) : (
                            <StyledProgressBarWrapper />
                        )}
                    </>

                    {productItems.length > 0 && isMiniBasketOpen && !isLoading && (
                        <StyledMiniBasketSimpleListWrapper>
                            <SimpleProductList
                                listId={RAPTOR_GET_RELATED_ITEMS}
                                listName={GTM_LIST_NAME.RAPTOR}
                                variant="small"
                                items={raptorProducts ?? []}
                                header={translate('basket.miniBasketRaptorTitle')}
                                productHandler={getProductHandler}
                                isLoading={isLoading}
                                isError={isError}
                                ProductLoader={ProductLoaderList}
                            />
                        </StyledMiniBasketSimpleListWrapper>
                    )}
                </StyledMiniBasketContent>
            </StyledMiniBasketWrapper>
        </Drawer>
    );
};
